import classNames from 'classnames'
import type { FC, PropsWithChildren } from 'react'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import type { LinkProps } from 'react-router-dom'

import type { EllipsisConfig, TextProps } from './types'
import type { PrivateThemeUiProps } from '../../../styles-system/create-component'
import createComponent from '../../../styles-system/create-component'
import type { ThemeUICSSObject } from '../../../theme-provider/types'
import { ellipsis } from '../../../themes/const'
import { positions } from '../../../utils/props-utils'
import { mergeRefs, reactNodeToString } from '../../../utils/react-helpers'

export function isTruncated(props: TextProps): boolean {
  return (
    (typeof props.ellipsis === 'boolean' && props.ellipsis) ||
    (typeof props.ellipsis === 'object' &&
      (props.ellipsis as EllipsisConfig).rows === 1)
  )
}

const TextElements = [
  'p',
  'a',
  'pre',
  'code',
  'kbd',
  'samp',
  'abbr',
  'i',
  'em',
  'var',
  'cite',
  'strong',
  'sub',
  'sup',
  'ins',
  'u',
  'mark',
  'b'
]

const StyledText = React.forwardRef<
  HTMLElement,
  TextProps & PrivateThemeUiProps
>(function StyledText(props, ref) {
  const {
    __themeKey = 'text',
    variant,
    as: component = 'span',
    __css,
    ...rest
  } = props
  const Component = useMemo(
    () => createComponent(component, 'csg-text'),
    [component]
  )
  const _variant = variant
    ? variant
    : TextElements.includes(component as string)
      ? `styles.${component}`
      : undefined

  const styles = useMemo(
    () => ({
      ...positions(props),
      ...__css
    }),
    [__css, props]
  )
  return (
    <Component
      ref={ref}
      __css={styles}
      {...rest}
      variant={_variant}
      __themeKey={__themeKey}
    />
  )
})

const defaultTextStyle: ThemeUICSSObject = {
  wordBreak: 'normal'
}

export const TextPrivate = React.forwardRef(function _TextPrivate(
  props: PropsWithChildren<TextProps & PrivateThemeUiProps> & {
    to?: LinkProps['to']
  },
  ref: React.Ref<HTMLElement>
) {
  const {
    className,
    lhCrop,
    title: _title,
    children,
    __css = defaultTextStyle,
    postContent,
    ...rest
  } = props
  const _isTruncated = isTruncated(props)
  const localRef = useRef<HTMLElement>(null)

  // Define state and function to update the value
  const [showTitle, setShowTitle] = useState(false)

  const title = useMemo(
    () =>
      _title ?? _isTruncated
        ? `${reactNodeToString(children)}${
            postContent ? ' ' + postContent : ''
          }`
        : '',
    [_title, _isTruncated, children, postContent]
  )

  const handleOnMouseOver = useCallback(() => {
    const compare =
      !!localRef.current &&
      localRef.current.scrollWidth > localRef.current.clientWidth
    setShowTitle(compare)
  }, [localRef])

  const wrap = lhCrop && _isTruncated

  // eslint-disable-next-line no-console
  // console.log(`----- render text [${children}]-----`)
  const styles = useMemo(
    () => ({
      ...(_isTruncated ? ellipsis : {}),
      ...__css
    }),
    [__css, _isTruncated]
  )

  const content = (
    <StyledText
      ref={wrap ? localRef : mergeRefs(ref, localRef)}
      className={
        wrap ? 'csg-tui-text-lhCrop' : classNames('csg-tui-text', className)
      }
      __css={styles}
      title={showTitle ? title ?? '' : ''}
      onMouseOver={handleOnMouseOver}
      {...(wrap ? {} : { lhCrop, ...rest })}
    >
      {children}
    </StyledText>
  )

  return wrap ? (
    <StyledText
      className={classNames('csg-tui-text', className)}
      ref={ref}
      lhCrop={lhCrop}
      {...rest}
    >
      {content}
    </StyledText>
  ) : (
    content
  )
})

const MemoText = React.memo(TextPrivate)

const Text: FC<TextProps> = React.forwardRef<HTMLElement, TextProps>(
  function Text(props, ref) {
    return <MemoText ref={ref} {...props} />
  }
)

export default Text
